"use client";

import dynamic from "next/dynamic";
import { useEffect, type ReactNode } from "react";

import { LazyPriceChart } from "@/components/charts/lazyCharts";
import type { ChartCandle } from "@/app/api/live/chart/route";
import DeferredMount from "@/components/ui/DeferredMount";
import { scheduleIdle } from "@/lib/scheduleIdle";
import HomeStatsPlaceholder from "./HomeStatsPlaceholder";
import LivePriceBox from "./LivePriceBox/LivePriceBox";
import type { NewsArticle } from "@/types/news";
import { NEWS_ENABLED } from "@/constants/news";

function MobilePricePlaceholder(): React.ReactElement {
  return (
    <div
      className="xl:hidden mb-6 min-h-[180px] border border-foreground/7 bg-foreground/4"
      aria-hidden
    />
  );
}

const StatsRow = dynamic(() => import("./StatsRow/StatsRow"), { ssr: false });
const BottomStats = dynamic(() => import("./BottomStats/BottomStats"), { ssr: false });
const CollapsedChartsSection = dynamic(
  () => import("./ChartsSection/CollapsedChartsSection"),
  { ssr: false },
);
const NewsSection = dynamic(() => import("./NewsSection/NewsSection"), { ssr: false });

interface HomeIndexClientProps {
  initialNews?: NewsArticle[];
  initialChartCandles?: ChartCandle[];
  chartSeoSection?: ReactNode;
}

export default function HomeIndexClient({
  initialNews = [],
  initialChartCandles,
  chartSeoSection,
}: HomeIndexClientProps): React.ReactElement {
  useEffect(() => {
    return scheduleIdle(() => {
      void import("@/components/pages/home/PriceChart/PriceChart");
    }, 2500);
  }, []);

  return (
    <div className="pt-4 lg:pt-6 grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)] gap-6 xl:gap-[30px] items-start relative">
      <aside className="hidden xl:flex flex-col gap-6 xl:gap-[30px] w-full min-w-0">
        <LivePriceBox />
        <div className="min-h-[200px] shrink-0">
          <CollapsedChartsSection />
        </div>
        {NEWS_ENABLED ? (
          <NewsSection initialArticles={initialNews} forceCollapsedChrome />
        ) : null}
      </aside>

      <section className="min-w-0 flex flex-col gap-0">
        <DeferredMount fallback={<MobilePricePlaceholder />}>
          <div className="xl:hidden mb-6 min-h-[180px]">
            <LivePriceBox />
          </div>
        </DeferredMount>
        <div className="flex flex-col xl:mb-0 mb-10 min-h-[543px] min-[1800px]:min-h-[706px]">
          <LazyPriceChart initialChartCandles={initialChartCandles} />
        </div>
        <DeferredMount fallback={<HomeStatsPlaceholder />} timeoutMs={3000}>
          <StatsRow />
          <BottomStats />
        </DeferredMount>
        {chartSeoSection}
      </section>
    </div>
  );
}
